import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Users, Zap, Gift, Headphones, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PartnerApplication } from '@/components/PartnerApplication';

const benefits = [
  {
    icon: Gift,
    title: 'Exclusive Pricing',
    description: 'Special partner rates on all IRL hosting services.',
  },
    {
    icon: Zap,
    title: 'Priority Support',
    description: 'Get dedicated support with faster response times.',
  },
  {
    icon: Users,
    title: 'Early Access',
    description: 'Be first to try new features and beta programs.',
  },
  {
    icon: Headphones,
    title: 'Direct Contact',
    description: 'Direct line to our team for custom requests and integrations.',
  },
];

const requirements = [
  'Minimum 100 average concurrent viewers on IRL streams',
  'Stream IRL content at least 4 times per month',
  'Active presence on Twitch, YouTube, or Kick',
  'Willingness to provide feedback and feature suggestions',
  'Professional conduct and positive community presence',
];

export function Partner() {
  const [showApplication, setShowApplication] = useState(false);

  if (showApplication) {
    return <PartnerApplication />;
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4"
          >
            <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium">
              Partner Program
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Stream with us as a Partner
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Join our exclusive partner program for special rates, 
            priority support, and early access to new features.
          </motion.p>
        </div>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Partner Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="p-6 bg-gray-900/50 border border-gray-800 rounded-2xl hover:border-gray-700 transition-colors"
              >
                <benefit.icon className="h-10 w-10 text-cyan-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Requirements
          </h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
            <ul className="space-y-4">
              {requirements.map((requirement, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center mt-0.5">
                    <Check className="h-4 w-4 text-cyan-400" />
                  </div>
                  <span className="text-gray-300">{requirement}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <p className="text-gray-400 mb-6">
            Think you qualify? Apply now and our team will review your application.
          </p>
          <Button
            size="lg"
            onClick={() => setShowApplication(true)}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-8"
          >
            Apply Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-500 text-sm">
            Applications are reviewed within 48 hours. We'll reach out via Discord 
            to discuss next steps if you're accepted.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
